"use client";

import { useEffect, useRef, useState } from "react";

import { resolveCharAsset, DEFAULT_CHAR_ASSET } from "@/engine/sprite-resolve";
import type { StatusKind } from "@/stores/game-store";

export type BattleSpriteState = "idle" | "attack" | "hurt" | "faint";
export type BattleSpriteOrientation = "front" | "back";

export interface BattleSpriteProps {
  speciesId: string;
  orientation: BattleSpriteOrientation;
  state?: BattleSpriteState;
  scale?: number;
  level?: number;
  /** Live current HP. A drop triggers a one-shot white flash + shake. */
  hp?: number;
  /** Active status condition — drives a subtle full-sprite tint + icon glow. */
  status?: StatusKind;
  /**
   * When true, render the AI-generated high-res battle portrait
   * (/assets/surggies/battle_<sprite>.png, sprite resolved from speciesId) as a WHOLE image scaled to the
   * battler box (object-fit contain, pixelated) instead of cropping a 16×32
   * frame from the char sheet. If that portrait is missing, we fall back to
   * resolveCharAsset(speciesId) and the exact 16×32 frame-scrub path below, so
   * species without a portrait render identically to today.
   */
  battlePortrait?: boolean;
}

// char_*.png are 64×128 sprite sheets: 4 animation cols × 4 direction rows
// (down=0, left=1, right=2, up=3), so each frame is 16×32 (see renderer.ts).
// A battler faces the viewer (front = "down" row) or away (back = "up" row),
// and we SCRUB the four columns of that row as a gentle idle step cycle.
const SHEET_COLS = 4;
const SHEET_ROWS = 4;
const FRAME_W = 16;
const FRAME_H = 32;

// Slow idle walk-cycle scrub: ~2.5 fps reads as a calm "breathing" step.
const IDLE_FRAME_MS = 380;
// Boustrophedon column order so the cycle eases in and out (0→1→2→3→2→1).
const IDLE_COLS = [0, 1, 2, 3, 2, 1];
// Attack lunge: a quick two-column flick at the apex of the lunge.
const ATTACK_COLS = [1, 2, 3, 2];
const ATTACK_FRAME_MS = 70;

// Subtle full-sprite tint while afflicted — palette-conform (PIXEL_STYLE):
// poison=mystic-purple, burn=alert-red, paralyze=gold, sleep=ocean-blue.
const STATUS_FILTER: Record<Exclude<StatusKind, "none">, string> = {
  poison: "saturate(1.3) hue-rotate(250deg) brightness(0.95)",
  burn: "saturate(1.4) sepia(0.4) hue-rotate(-25deg) brightness(1.02)",
  paralyze: "saturate(1.5) sepia(0.6) hue-rotate(-5deg) brightness(1.06)",
  sleep: "saturate(0.85) hue-rotate(190deg) brightness(0.92)",
};

const STATUS_GLOW: Record<Exclude<StatusKind, "none">, string> = {
  poison: "drop-shadow(0 0 3px rgba(160, 74, 184, 0.6))",
  burn: "drop-shadow(0 0 3px rgba(168, 58, 42, 0.6))",
  paralyze: "drop-shadow(0 0 3px rgba(197, 168, 58, 0.6))",
  sleep: "drop-shadow(0 0 3px rgba(61, 104, 144, 0.6))",
};

// Battle-portrait URL for the AI-generated high-res art (whole-image render).
// Reuse the SAME speciesId normalization as the char sheet (numeric id,
// species name, or "surggie_N" key all resolve to char_surggie_N), then swap
// the char_ prefix for the high-res portrait → battle_surggie_N.png. This keeps
// portraits keyed by sprite (1..12), so species that share a sprite share art,
// and a missing portrait still onError-falls back to the char sheet below.
function battlePortraitUrl(speciesId: string): string {
  return resolveCharAsset(speciesId).replace("/char_", "/battle_");
}

export function BattleSprite({
  speciesId,
  orientation,
  state = "idle",
  scale = 3,
  level,
  hp,
  status = "none",
  battlePortrait = false,
}: BattleSpriteProps) {
  // portraitMode is the live render mode: true → whole high-res portrait,
  // false → today's 16×32 char-sheet frame scrub. It starts as the requested
  // mode and flips to false permanently if the portrait image fails to load.
  const [portraitMode, setPortraitMode] = useState(battlePortrait);
  const [src, setSrc] = useState<string>(() =>
    battlePortrait ? battlePortraitUrl(speciesId) : resolveCharAsset(speciesId),
  );
  const [triedFallback, setTriedFallback] = useState(false);

  useEffect(() => {
    setPortraitMode(battlePortrait);
    setSrc(
      battlePortrait
        ? battlePortraitUrl(speciesId)
        : resolveCharAsset(speciesId),
    );
    setTriedFallback(false);
  }, [speciesId, battlePortrait]);

  // Integer upscale so pixel art stays crisp. scale=3 → 5× → 80×160 battler.
  const px = Math.max(4, Math.round(scale * 1.5));
  const boxW = FRAME_W * px;
  const boxH = FRAME_H * px;

  const row = orientation === "front" ? 0 : 3; // down faces viewer; up faces away

  // ── Frame scrubbing ───────────────────────────────────────────────────────
  // Idle cycles slowly through IDLE_COLS; attack plays ATTACK_COLS once and
  // settles back to col 0; hurt/faint hold col 0 (the CSS transform carries
  // the motion). Driven by a self-scheduling timer so it never depends on RAF
  // budget and stays at a deliberate, pixel-art-friendly cadence.
  const [col, setCol] = useState(0);
  useEffect(() => {
    if (state === "idle") {
      let i = 0;
      setCol(IDLE_COLS[0]);
      const id = window.setInterval(() => {
        i = (i + 1) % IDLE_COLS.length;
        setCol(IDLE_COLS[i]);
      }, IDLE_FRAME_MS);
      return () => window.clearInterval(id);
    }
    if (state === "attack") {
      let i = 0;
      setCol(ATTACK_COLS[0]);
      const id = window.setInterval(() => {
        i += 1;
        if (i >= ATTACK_COLS.length) {
          setCol(0);
          window.clearInterval(id);
          return;
        }
        setCol(ATTACK_COLS[i]);
      }, ATTACK_FRAME_MS);
      return () => window.clearInterval(id);
    }
    // hurt / faint: hold the rest frame.
    setCol(0);
  }, [state]);

  // ── On-hit flash + shake ──────────────────────────────────────────────────
  // Watch the hp prop; any drop fires a one-shot ~120ms white flash + 4px
  // shake on THIS sprite, independent of the engine state machine.
  const [hitNonce, setHitNonce] = useState(0);
  const prevHpRef = useRef<number | undefined>(hp);
  useEffect(() => {
    const prev = prevHpRef.current;
    prevHpRef.current = hp;
    if (prev === undefined || hp === undefined) return;
    if (hp < prev) setHitNonce((n) => n + 1);
  }, [hp]);

  const lunge =
    state === "attack" ? (orientation === "back" ? 16 : -16) : 0;

  // Per-state motion. Attack uses a transform transition for the lunge; the
  // hit-flash shake is a keyed keyframe so each hit re-triggers it.
  const stateTransform =
    state === "attack"
      ? `translateX(${lunge}px)`
      : state === "faint"
        ? "rotate(90deg)"
        : "translateX(0)";

  const afflicted = status !== "none";
  const baseFilter = afflicted ? STATUS_FILTER[status] : undefined;
  const glowFilter = afflicted ? STATUS_GLOW[status] : undefined;
  // Hurt recoil reuses the red wash; the hit flash overlay handles the white.
  const hurtFilter =
    state === "hurt"
      ? "brightness(1.1) saturate(1.4) sepia(0.6) hue-rotate(-40deg)"
      : undefined;
  const composedFilter =
    [hurtFilter, baseFilter, glowFilter].filter(Boolean).join(" ") || undefined;

  return (
    <div
      className="relative inline-block select-none"
      style={{ width: `${boxW}px`, height: `${boxH}px` }}
      data-species={speciesId}
      data-orientation={orientation}
      data-state={state}
      data-status={status}
    >
      <style>{`
        @keyframes surggie-bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes surggie-hit-shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-4px); }
          40% { transform: translateX(4px); }
          60% { transform: translateX(-3px); }
          80% { transform: translateX(3px); }
        }
        @keyframes surggie-hurt {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          50% { transform: translateX(4px); }
          75% { transform: translateX(-3px); }
        }
        @keyframes surggie-hit-flash {
          0% { opacity: 0.85; }
          100% { opacity: 0; }
        }
      `}</style>

      {/* Elliptical ground shadow */}
      <div
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2"
        style={{
          bottom: `${Math.max(2, Math.floor(px * 1.5))}px`,
          width: `${Math.floor(boxW * 0.7)}px`,
          height: "8px",
          borderRadius: "50%",
          background: "rgba(0, 0, 0, 0.3)",
          filter: "blur(3px)",
          zIndex: 0,
        }}
      />

      {/* Motion wrapper: carries lunge / faint / idle-bob / hurt / hit-shake.
          The hit-shake is keyed on hitNonce so a repeated hit re-fires it. */}
      <div
        key={`motion-${hitNonce}`}
        className="absolute inset-0"
        style={{
          transform: stateTransform,
          transition: state === "attack" ? "transform 90ms ease-out" : undefined,
          animation:
            hitNonce > 0 && state !== "faint"
              ? "surggie-hit-shake 140ms steps(1, end)"
              : state === "idle"
                ? "surggie-bob 1.6s ease-in-out infinite"
                : state === "hurt"
                  ? "surggie-hurt 300ms linear"
                  : undefined,
          zIndex: 1,
        }}
      >
        {/* Single-frame window into the sheet (overflow-clipped). */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{
            imageRendering: "pixelated",
            filter: composedFilter,
            opacity: state === "faint" ? 0 : 1,
            transition:
              state === "faint" ? "opacity 500ms ease-out" : undefined,
          }}
        >
          <img
            src={src}
            alt=""
            draggable={false}
            onError={() => {
              // Portrait missing → drop to the char-sheet frame-scrub path
              // (today's exact behavior) so species without a battle_<id>.png
              // look identical to now.
              if (portraitMode) {
                setPortraitMode(false);
                setSrc(resolveCharAsset(speciesId));
                return;
              }
              if (!triedFallback) {
                setTriedFallback(true);
                setSrc(DEFAULT_CHAR_ASSET);
              }
            }}
            style={
              portraitMode
                ? {
                    // Whole high-res portrait scaled to fill the battler box.
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    imageRendering: "pixelated",
                  }
                : {
                    // 16×32 frame window into the 64×128 char sheet.
                    position: "absolute",
                    left: `${-(col * FRAME_W * px)}px`,
                    top: `${-(row * FRAME_H * px)}px`,
                    width: `${SHEET_COLS * FRAME_W * px}px`,
                    height: `${SHEET_ROWS * FRAME_H * px}px`,
                    maxWidth: "none",
                    imageRendering: "pixelated",
                  }
            }
          />
        </div>

        {/* One-shot white hit flash — overlay clipped to the sprite box via
            mix-blend so only the lit pixels flash, not the transparent gutter. */}
        {hitNonce > 0 && state !== "faint" && (
          <div
            key={`flash-${hitNonce}`}
            aria-hidden
            className="absolute inset-0"
            style={{
              background: "#f5f1e8",
              mixBlendMode: "screen",
              pointerEvents: "none",
              animation: "surggie-hit-flash 120ms ease-out forwards",
              imageRendering: "pixelated",
            }}
          />
        )}
      </div>

      {/* Level overlay */}
      {typeof level === "number" && (
        <div
          className="absolute top-0 right-0 px-1.5 py-0.5 text-[10px] font-mono font-bold tracking-wider"
          style={{
            color: "#f5f1e8",
            background: "rgba(28, 28, 32, 0.78)",
            border: "1px solid rgba(245, 241, 232, 0.25)",
            borderRadius: "2px",
            textShadow: "1px 1px 0 rgba(0,0,0,0.6)",
            zIndex: 2,
            lineHeight: 1,
          }}
        >
          Lv{level}
        </div>
      )}
    </div>
  );
}

export default BattleSprite;
